import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, BarChart3, CalendarDays, ChevronDown, Loader2, Settings } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { useDailyStats } from '@/features/agent-metrics/useAgentMetrics';
import { useQueue, useTakeNext } from '@/features/queue/useQueue';
import { queueApi } from '@/features/queue/queue.api';
import type { QueueItem } from '@/types/api';
import { shortCinHash } from '@/lib/format';
import { useBookings } from '@/features/bookings/useBookings';
import { useBookingAlerts } from '@/features/bookings/useBookingAlerts';
import { screeningApi } from '@/features/screening/screening.api';
import { clientsApi, type PatientSearchResult } from '@/features/clients/clients.api';
import { apiCall, ApiError } from '@/lib/api-client';
import { useActiveCallStore } from '@/stores/active-call.store';
import { toast } from '@/stores/toast.store';
import { useAuthStore } from '@/stores/auth.store';
import { useT, useLang } from '@/i18n';
import { cn } from '@/lib/utils';

import { Kicker, NavLayer, NavPill, BTN_PRI, BTN_SEC, INP } from '@/components/brand/brand';
import { Meter } from '@/components/agent/F5Bits';
import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { ListModalChassis, type ListModalRow } from '@/components/chassis/ListModal';
import { useClaimSpecific } from '@/features/queue/queueRowDetails';
import type { BookingRow } from '@/features/bookings/bookings.api';
import { RangoCard } from '@/components/rango/RangoCard';

/**
 * F5-R8a · «Inicio» del agente (dibujo «Agente v2 — Header») +
 * F5-R9a · «Mi cola» SE FUNDE ACÁ (encargo verbatim del CEO 2026-08-30:
 * «Unifica "MI COLA" con "INICIO", se ve muy vacío así, solo queda vivo
 * inicio y lo que está dentro de mi cola pasa a "INICIO" … esas cosas ponlas
 * en medio de "MI RANGO" y "Accesos", no vas a eliminar nada solo a
 * unificarlas»). Muere la pestaña /queue (la ruta queda como redirect que
 * preserva state) — NADA de contenido muere. Acá viven:
 *
 *  - el saludo,
 *  - la tarjeta «Mi rango» (RangoCard — la escalera del dibujo R8b),
 *  - «Mi meta de hoy» (misma fuente F-5 vía server),
 *  - LA COLA COMPLETA (MOVIDA de QueuePage en R9a): tarjeta navy
 *    take-next/resume con la reconciliación R2 del active-call store, los
 *    botones «Mis citas» y «Ver mi cola (N)» (ListModal de solo lectura con
 *    claim por fila), y el toast de screening enviado,
 *  - lo que exige acción hoy: citas de hoy (franja ámbar) y «Esperando
 *    subida a FindHelp» (la franja DEGRADADA de F5-R1a, intacta),
 *  - los accesos: Mi agenda · Mis números · Mi perfil y ajustes (la tarjeta
 *    «Mi cola» murió con la pestaña — la cola vive arriba),
 *  - la búsqueda de caso (CaseSearch, MOVIDA de QueuePage — al fondo).
 *
 * useBookingAlerts() se monta EXACTAMENTE UNA VEZ acá (su dedupe es useRef
 * por montaje: dos montajes = toast+chime dobles). Cero lógica de negocio
 * nueva: todo es reparto de superficies vivas.
 */
/**
 * COLA-AGENTE A1 — cuantas filas por pagina pide la cola.
 *
 * Es el MISMO numero para el fetch y para el chasis a proposito: si el
 * server trae 25 y la pantalla partiera de a 10, el pager contaria mal.
 * El server acepta hasta 100 por pagina (agents.controller.ts) — 25 es
 * el alto del modal, no un limite tecnico.
 */
const QUEUE_PAGE = 25;

export function InicioPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const firstName = user ? (user.fullName.split(' ')[0] ?? '') : '';

  const dailyQ = useDailyStats();
  const bookingsTodayQ = useBookings('today');
  // F5-R9a: ÚNICO montaje en todo web/src (vivía en QueuePage).
  useBookingAlerts();

  const activeCall = useActiveCallStore((s) => s.current);
  const activeScreeningId = useActiveCallStore((s) => s.activeScreeningId);
  const setCurrent = useActiveCallStore((s) => s.setCurrent);
  const clearActive = useActiveCallStore((s) => s.clear);

  // La lista murió: la cola se consume por el botón. Pedimos 1 fila solo para
  // el COUNT real (total) y el estado vacío. (MOVIDO de QueuePage.)
  const queue = useQueue({ limit: 1, offset: 0 });
  const queueTotal = queue.data?.total ?? queue.data?.count ?? 0;
  const takeNext = useTakeNext();

  // R2 (caminata CEO): el store de llamada abierta es SOLO cliente y mentía
  // al volver acá («Reanudar» fantasma tras parquear/inelegible/salir del
  // screening — había que apretar Actualizar). Al montar se valida contra
  // el server: si el assignment ya no está in_progress (o ya no es tuyo),
  // el store se limpia y la tarjeta vuelve sola a «Tomar el siguiente».
  const staleCheckQ = useQuery({
    queryKey: ['queue', 'active-check', activeCall?.assignmentId ?? ''],
    queryFn: () =>
      apiCall<{ status?: string }>(
        `/agents/me/queue/${activeCall!.assignmentId}/details`,
      ),
    enabled: !!activeCall,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
  useEffect(() => {
    if (!activeCall) return;
    if (staleCheckQ.isError) { clearActive(); return; }
    const st = staleCheckQ.data?.status;
    if (st && st !== 'in_progress') clearActive();
  }, [activeCall, staleCheckQ.isError, staleCheckQ.data, clearActive]);

  // Progreso de la llamada abierta (para «{nombre} · dominio N de 9»).
  const activeProgressQ = useQuery({
    queryKey: ['screening', activeScreeningId, 'progress-peek'],
    queryFn: () => screeningApi.get(activeScreeningId as string),
    enabled: !!activeScreeningId,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
  // R2: si el screening «abierto» ya no existe (cancel-beacon al salir por
  // el logo, o el reaper), el peek da 404 — el store se limpia solo.
  useEffect(() => {
    if (!activeScreeningId) return;
    const e = activeProgressQ.error;
    if (e instanceof ApiError && e.status === 404) clearActive();
  }, [activeScreeningId, activeProgressQ.error, clearActive]);

  // Toast al volver de un screening enviado (se conserva — el redirect de
  // /queue preserva el location.state, así que llega acá intacto).
  useEffect(() => {
    const state = location.state as { justCompleted?: boolean } | null;
    if (state?.justCompleted) {
      toast.success(t('queue.toastSubmitted'), t('queue.toastSubmittedDesc'));
      window.history.replaceState({}, '');
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cluster H v2 SUB-G — screenings de hogar esperando validación del
  // uploader (misma queryKey, mismo endpoint).
  const pendingQ = useQuery({
    queryKey: ['agents', 'me', 'pending-validations'],
    queryFn: () =>
      apiCall<Array<{
        subScreeningId: string;
        submittedAt: string;
        hoursSinceSubmit: number;
        hmFullName: string;
        parentClientLastName: string;
        parentClientCinLast6: string;
      }>>('/agents/me/pending-validations'),
    staleTime: 30_000,
    refetchInterval: 120_000,
  });
  const pendRows = pendingQ.data ?? [];

  const submittedToday = dailyQ.data?.submittedToday ?? 0;
  // F-5 (CEO 2026-08-27): UNA sola meta diaria — la que manda config, vía el
  // server. Nada se cablea acá.
  const dailyTarget = dailyQ.data?.target ?? 0;
  const metaDone = dailyTarget > 0 && submittedToday >= dailyTarget;

  // Citas de hoy — solo las vivas (UNA sola derivación: alimenta la franja
  // ámbar Y el botón «Mis citas» — F5-R9a consolidó el memo duplicado).
  const apptsToday = useMemo(() => {
    const rows = bookingsTodayQ.data ?? [];
    return rows
      .filter((b) => b.status === 'scheduled' || b.status === 'rescheduled')
      .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
  }, [bookingsTodayQ.data]);

  const [citaOpen, setCitaOpen] = useState<BookingRow | null>(null);
  // F5-R1a (encargo 3) — la franja FindHelp degradada, colapsada (conserva).
  const [fhOpen, setFhOpen] = useState(false);
  // F5-R1a (encargo 2, verbatim CEO: «que exista dentro de algún botón donde
  // puedan ver eso») — la lista de pendientes DETRÁS de un botón, como modal
  // de solo lectura.
  const [queueListOpen, setQueueListOpen] = useState(false);

  const handleTakeNext = async (): Promise<void> => {
    try {
      const next = await takeNext.mutateAsync();
      setCurrent(next);
      navigate(`/pre-call/${next.assignmentId}`);
    } catch {
      // el error se pinta bajo el botón
    }
  };
  const handleResume = (): void => {
    if (!activeCall) return;
    if (activeScreeningId) navigate(`/screening/${activeScreeningId}`);
    else navigate(`/pre-call/${activeCall.assignmentId}`);
  };

  const hasActive = !!activeCall;

  // Dominio N de 9 de la llamada abierta (respondidos + 1, tope 9).
  const activeDomainN = activeProgressQ.data
    ? Math.min(9, (activeProgressQ.data.answers?.length ?? 0) + 1)
    : null;
  const activeMinutes = activeCall?.sessionStartedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(activeCall.sessionStartedAt).getTime()) / 60_000))
    : null;

  const showAmber = apptsToday.length > 0;

  return (
    <div className="max-w-[1080px] mx-auto font-body text-nav-navy">
      {/* 1 · SALUDO — pageCopy del dibujo: «Buen día, {nombre}» + «Tu rango,
          lo que exige acción hoy y el acceso a la cola.» */}
      <header className="animate-fade-up-slow mb-4">
        <h1 className="font-head text-2xl font-extrabold tracking-[-0.02em] m-0">
          {t('agentF5.q.greet', { name: firstName })}
        </h1>
        <p className="text-sm text-nav-slate mt-1 m-0">{t('agentF5.r8a.homeSub')}</p>
      </header>

      {/* 2 · MI RANGO + MI META DE HOY, lado a lado. */}
      <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))' }}>
        <RangoCard role="agent" />

        {/* «Mi meta de hoy» — MOVIDA de QueuePage tal cual (F-5 intacta). */}
        <section
          className="bg-white border-[1.5px] border-nav-card rounded-xl px-[22px] py-5 animate-fade-up-slow"
          style={{ animationDelay: '60ms' }}
        >
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <p className="font-head text-[13.5px] font-bold m-0">{t('agentF5.q.metaLabel')}</p>
            <p className="font-head text-sm font-extrabold tabular-nums m-0">
              {dailyQ.isLoading
                ? '—'
                : t('agentF5.q.metaValue', { n: submittedToday, target: dailyTarget })}
            </p>
          </div>
          <Meter
            pct={dailyTarget > 0 ? (submittedToday / dailyTarget) * 100 : 0}
            tone={metaDone ? 'sage' : 'teal'}
            className="mt-2.5"
          />
          <p className="text-[12.5px] text-nav-slate mt-2.5 m-0">
            {metaDone ? t('agentF5.q.metaDone', { target: dailyTarget }) : t('agentF5.q.metaNote')}
          </p>
        </section>
      </div>

      {/* 3 · LA COLA — MOVIDA COMPLETA de QueuePage (F5-R9a): en medio de
          «Mi rango» y «Accesos», ANTES de la franja ámbar (el CTA navy es la
          acción default). El subtítulo vivo de la pestaña vieja queda como
          copete del bloque. */}
      <section className="mt-[30px]">
        <p className="text-sm text-nav-slate m-0 mb-3.5" aria-live="polite">
          {hasActive
            ? t('agentF5.q.greetActive')
            : t('agentF5.q.greetIdle', { appts: apptsToday.length, queue: queueTotal })}
        </p>

        {/* TARJETA NAVY «LO QUE SIGUE» — el único botón primario. */}
        {queue.error ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-xl mb-3.5">
            <ErrorState
              icon={<span aria-hidden="true">!</span>}
              title={t('agentF5.q.errTitle')}
              body={t('agentF5.q.errBody')}
              action={{ label: t('agentF5.q.errRetry'), onClick: () => void queue.refetch() }}
            />
          </div>
        ) : !hasActive && queue.data && queueTotal === 0 ? (
          <div className="bg-white border-[1.5px] border-nav-sage rounded-xl mb-3.5 animate-fade-up-slow">
            <EmptyState
              icon={<span aria-hidden="true">✓</span>}
              title={t('agentF5.q.emptyTitle')}
              body={t('agentF5.q.emptyBody')}
              action={{ label: t('agentF5.q.emptyCta'), onClick: () => navigate('/agenda') }}
            />
          </div>
        ) : (
          <section
            className="bg-nav-navy rounded-xl px-7 py-[26px] mb-3.5 flex flex-wrap items-start justify-between gap-6 animate-fade-up-slow"
            aria-live="polite"
          >
            <div className="flex-1 min-w-[280px]">
              <p
                className={cn(
                  'font-head font-bold text-[10.5px] tracking-[.14em] uppercase m-0 mb-2 flex items-center gap-2',
                  hasActive ? 'text-[#d9a540]' : 'text-[#7fc3c5]',
                )}
              >
                {hasActive && (
                  <span
                    aria-hidden="true"
                    className="w-2 h-2 rounded-full bg-[#d9a540] motion-safe:animate-pulse"
                  />
                )}
                {hasActive ? t('agentF5.q.kickerActive') : t('agentF5.q.kickerIdle')}
              </p>
              <h2 className="font-head text-[22px] font-extrabold text-white m-0 mb-2 leading-tight">
                {hasActive
                  ? activeDomainN != null && activeScreeningId
                    ? t('agentF5.q.heroTitleActiveDomain', {
                        name: `${activeCall!.client.firstName} ${activeCall!.client.lastName}`,
                        n: activeDomainN,
                        total: 9,
                      })
                    : `${activeCall!.client.firstName} ${activeCall!.client.lastName}`
                  : t('agentF5.q.heroTitleIdle')}
              </h2>
              <p className="text-sm leading-relaxed text-[#c9d6e2] m-0 max-w-[520px]">
                {hasActive
                  ? activeScreeningId && activeMinutes != null
                    ? t('agentF5.q.heroBodyActiveScreening', { min: activeMinutes })
                    : t('agentF5.q.heroBodyActivePrecall')
                  : t('agentF5.q.heroBodyIdle', { n: queueTotal })}
              </p>
            </div>
            <div className="flex flex-col items-end gap-3.5">
              <button
                type="button"
                onClick={hasActive ? handleResume : () => void handleTakeNext()}
                disabled={!hasActive && (takeNext.isPending || queue.isLoading)}
                className={cn(
                  'inline-flex items-center gap-2.5 min-h-[52px] px-6 rounded-lg border-[1.5px] border-white',
                  'bg-white text-nav-navy font-head font-bold text-[15px] cursor-pointer transition-colors',
                  'hover:bg-[#f4fafa] hover:border-[#f4fafa] active:scale-[0.98]',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7fc3c5]',
                )}
              >
                {takeNext.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : null}
                {hasActive ? t('agentF5.q.ctaActive') : t('agentF5.q.ctaIdle')}
                <ArrowRight className="h-[17px] w-[17px]" strokeWidth={2} aria-hidden />
              </button>
              <p className="text-[12.5px] text-[#9fb6c8] text-right max-w-[220px] m-0">
                {hasActive ? t('agentF5.q.ctaNoteActive') : t('agentF5.q.ctaNoteIdle')}
              </p>
              {takeNext.error != null && (
                <p className="text-[12.5px] text-[#f4d9d5] text-right m-0" role="alert">
                  {takeNext.error instanceof ApiError && takeNext.error.status === 404
                    ? t('queue.noPending')
                    : (takeNext.error as Error).message}
                </p>
              )}
            </div>
          </section>
        )}

        {/* F5-R1a (encargo 2) — «Ver mi cola (N)»: botón secundario pegado a
            la tarjeta navy que abre la lista de pendientes en un ListModal
            del chasis. Solo lectura — tomar sigue siendo el botón grande. */}
        {/* R4 (caminata CEO): «el agente necesita ver sus bookings… no hay
            botón para acceder desde el dashboard» — acceso PERMANENTE a Mis
            citas junto a Ver mi cola («Mis citas» → /agenda). */}
        <div className="flex justify-end gap-2 -mt-1">
          <button
            type="button"
            onClick={() => navigate('/agenda')}
            className={cn(BTN_SEC, 'min-h-[44px] tabular-nums')}
          >
            {t('agentF5.q.viewBookings', { n: apptsToday.length })}
          </button>
          {queueTotal > 0 && (
          <button
            type="button"
            onClick={() => setQueueListOpen(true)}
            className={cn(BTN_SEC, 'min-h-[44px] tabular-nums')}
          >
            {t('agentF5.r1a.viewQueue', { n: queueTotal })}
          </button>
          )}
        </div>
      </section>

      {/* 4 · FRANJA ÁMBAR «EXIGE ACCIÓN HOY» — se auto-oculta vacía, rótulo
          incluido. */}
      {showAmber && (
        <section className="animate-fade-up-slow" style={{ animationDelay: '120ms' }}>
          <p className="font-head font-bold text-[10.5px] tracking-[.14em] uppercase text-[#7a5410] mt-[30px] mb-3.5 m-0">
            {t('agentF5.q.amberKicker')}
          </p>
          <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))' }}>
            <div className="bg-nav-amberTint border-[1.5px] border-nav-amber rounded-xl px-[22px] py-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="font-head text-[13.5px] font-bold text-[#7a5410] m-0">
                  {t('agentF5.q.apptsTitle')}
                </p>
                <span className="inline-flex items-center justify-center min-w-[30px] min-h-[30px] px-2 rounded-full bg-white border border-[#e6cf9a] font-head text-[13px] font-extrabold tabular-nums text-[#7a5410]">
                  {apptsToday.length}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {apptsToday.map((b) => (
                  <ApptRow key={b.id} booking={b} t={t} lang={lang} onOpen={() => setCitaOpen(b)} />
                ))}
              </div>
              <p className="text-[12.5px] text-[#7a5410] mt-3 m-0">{t('agentF5.q.apptsFoot')}</p>
            </div>
          </div>
        </section>
      )}

      {/* 5 · ACCESOS — cada pestaña con su tarjeta, más «Mi perfil y ajustes»
          (el gear salió de la barra; el acceso vive acá). F5-R9a: la tarjeta
          «Mi cola» murió con la pestaña — la cola vive arriba, en esta misma
          pantalla. */}
      <section className="animate-fade-up-slow" style={{ animationDelay: '180ms' }}>
        <Kicker className="mt-[30px] mb-3.5">{t('agentF5.r8a.accessKicker')}</Kicker>
        <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <AccessCard
            to="/agenda"
            icon={<CalendarDays className="h-5 w-5 text-nav-tealDark" aria-hidden />}
            title={t('agentF5.r8a.tabAgenda')}
            desc={t('agentF5.r8a.accessAgendaDesc')}
          />
          <AccessCard
            to="/numeros"
            icon={<BarChart3 className="h-5 w-5 text-nav-tealDark" aria-hidden />}
            title={t('agentF5.r8a.tabNumbers')}
            desc={t('agentF5.r8a.accessNumbersDesc')}
          />
          <AccessCard
            to="/settings"
            icon={<Settings className="h-5 w-5 text-nav-tealDark" aria-hidden />}
            title={t('agentF5.r8a.accessSettings')}
            desc={t('agentF5.r8a.accessSettingsDesc')}
          />
        </div>
      </section>

      {/* 6 · FRANJA «BUSCAR UN CASO» — campo siempre visible (MOVIDA de
          QueuePage en F5-R9a, al fondo, antes de «Esperando subida»). */}
      <CaseSearch t={t} />

      {/* 7 · «Esperando subida a FindHelp» — la franja DEGRADADA de F5-R1a
          (encargo 3, conserva E-23): al fondo, línea discreta colapsable en
          neutro. Dato y fuente iguales. */}
      {pendRows.length > 0 && (
        <section className="mt-[30px]">
          <button
            type="button"
            onClick={() => setFhOpen((v) => !v)}
            aria-expanded={fhOpen}
            className="w-full flex items-center justify-between gap-3 min-h-[44px] px-4 py-2.5 bg-nav-soft border-[1.5px] border-nav-card rounded-[10px] font-head text-[12.5px] font-bold text-nav-slate cursor-pointer hover:bg-nav-softer hover:border-nav-line transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal"
          >
            <span className="tabular-nums">{t('agentF5.r1a.fhPendingToggle', { n: pendRows.length })}</span>
            <ChevronDown
              className={cn('h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none', fhOpen && 'rotate-180')}
              aria-hidden
            />
          </button>
          {fhOpen && (
            <div className="mt-2 bg-white border-[1.5px] border-nav-card rounded-[10px] px-4 py-3">
              <p className="text-[12.5px] text-nav-slate m-0 mb-2.5">{t('agentF5.r1a.fhPendingNote')}</p>
              <div className="flex flex-col gap-2">
                {pendRows.map((r) => (
                  <div
                    key={r.subScreeningId}
                    className="flex items-center justify-between gap-3 flex-wrap min-h-[44px] px-3 py-2 bg-nav-softer border border-nav-hair rounded-[10px]"
                  >
                    <span className="font-head text-[13px] font-bold m-0 truncate">
                      {r.hmFullName || `#${r.subScreeningId}`}
                    </span>
                    <span className="text-xs text-nav-slate tabular-nums shrink-0">
                      {t('agentF5.q.pendSent', { when: relativeDayTime(r.submittedAt, t, lang) })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <CitaDetailModal booking={citaOpen} onClose={() => setCitaOpen(null)} t={t} lang={lang} />

      {/* Modal de la cola (conservado — F5-R9a lo trajo con la cola). */}
      <QueueListModal
        open={queueListOpen}
        onClose={() => setQueueListOpen(false)}
        total={queueTotal}
        hasActive={hasActive}
        t={t}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Acceso de Inicio: tarjeta blanca con hover -3px y borde teal (motion F5). */
function AccessCard({
  to,
  icon,
  title,
  desc,
}: {
  to: string;
  icon: JSX.Element;
  title: string;
  desc: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      className={cn(
        'block bg-white border-[1.5px] border-nav-card rounded-xl px-5 py-[18px] no-underline text-nav-navy',
        'transition-all duration-[180ms] hover:-translate-y-[3px] hover:border-nav-teal active:scale-[0.98]',
        'motion-reduce:hover:translate-y-0',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
      )}
    >
      <div className="h-10 w-10 rounded-[10px] bg-nav-tealTint flex items-center justify-center mb-3">
        {icon}
      </div>
      <p className="font-head text-[14.5px] font-bold m-0">{title}</p>
      <p className="text-[12.5px] text-nav-slate mt-1 m-0">{desc}</p>
    </Link>
  );
}

function relativeDayTime(
  iso: string,
  t: ReturnType<typeof useT>,
  lang: 'en' | 'es',
): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString(lang === 'es' ? 'es' : [], { hour: '2-digit', minute: '2-digit' });
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today.getTime() - day.getTime()) / 86_400_000);
  if (diffDays === 0) return t('agentF5.q.whenToday', { time });
  if (diffDays === 1) return t('agentF5.q.whenYesterday', { time });
  return t('agentF5.q.whenOn', {
    date: d.toLocaleDateString(lang === 'es' ? 'es' : [], { day: 'numeric', month: 'short' }),
  });
}

function ApptRow({
  booking,
  t,
  lang,
  onOpen,
}: {
  booking: BookingRow;
  t: ReturnType<typeof useT>;
  lang: 'en' | 'es';
  onOpen: () => void;
}): JSX.Element {
  const d = new Date(booking.scheduledAt);
  const time = d.toLocaleTimeString(lang === 'es' ? 'es' : [], { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="flex items-center gap-[11px] flex-wrap min-h-[56px] px-3 py-[9px] bg-white border-[1.5px] border-[#e6cf9a] rounded-[10px]">
      <span className="inline-flex items-center justify-center min-w-[60px] min-h-[44px] px-2 rounded-lg bg-nav-amberTint border border-[#e6cf9a] font-head text-[13px] font-extrabold tabular-nums text-[#7a5410]">
        {time}
      </span>
      <span className="font-head text-[13px] font-bold truncate">
        {booking.patient?.lastNameDecrypted ?? '—'}
      </span>
      <span className="text-xs text-nav-slate tabular-nums min-w-0 truncate">
        {t('agentF5.q.apptMeta', {
          id: booking.patient?.mrnLast6 ?? '????',
          when: relativeDayTime(booking.createdAt, t, lang),
        })}
      </span>
      <button
        type="button"
        onClick={onOpen}
        className="ml-auto shrink-0 min-h-[44px] px-3.5 rounded-lg bg-white border-[1.5px] border-nav-amber font-head text-[12.5px] font-bold text-[#7a5410] cursor-pointer hover:bg-nav-amberTint transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal"
      >
        {t('agentF5.q.apptOpen')}
      </button>
    </div>
  );
}

/**
 * «Abrir la cita» abre el detalle de ESTA cita — hora y nota del agendamiento
 * (el teléfono acordado no viene en la fila del server: pendiente de server).
 * Desde acá se entra al caso por el perfil. (MOVIDO de QueuePage tal cual.)
 */
function CitaDetailModal({
  booking,
  onClose,
  t,
  lang,
}: {
  booking: BookingRow | null;
  onClose: () => void;
  t: ReturnType<typeof useT>;
  lang: 'en' | 'es';
}): JSX.Element | null {
  const navigate = useNavigate();
  if (!booking) return null;
  const d = new Date(booking.scheduledAt);
  return (
    <NavLayer
      open={!!booking}
      onClose={onClose}
      kicker={t('agentF5.q.citaKicker')}
      title={booking.patient?.lastNameDecrypted ?? '—'}
      subtitle={`ID ****${booking.patient?.mrnLast6 ?? '????'}`}
    >
      <div className="bg-nav-softer border-[1.5px] border-nav-card rounded-xl px-4 py-3.5 mb-3">
        <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-teal m-0 mb-1">
          {t('agentF5.q.citaTime')}
        </p>
        <p className="font-head text-[15px] font-bold tabular-nums m-0">
          {d.toLocaleDateString(lang === 'es' ? 'es' : [], { weekday: 'long', day: 'numeric', month: 'long' })}
          {' · '}
          {d.toLocaleTimeString(lang === 'es' ? 'es' : [], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
      <div className="bg-nav-softer border-[1.5px] border-nav-card rounded-xl px-4 py-3.5 mb-4">
        <p className="font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-teal m-0 mb-1">
          {t('agentF5.q.citaNotes')}
        </p>
        <p className="text-[13.5px] m-0">{booking.notes || t('agentF5.q.citaNoNotes')}</p>
      </div>
      <div className="flex justify-end gap-2.5">
        <button type="button" className={BTN_SEC} onClick={onClose}>
          {t('agentF5.q.citaClose')}
        </button>
        <button
          type="button"
          className={BTN_PRI}
          onClick={() => navigate(`/clients/${booking.patientId}`)}
        >
          {t('agentF5.q.citaProfile')}
        </button>
      </div>
    </NavLayer>
  );
}

/**
 * F5-R1a (encargo 2) — la lista de pendientes que murió en T7, de vuelta
 * DETRÁS de un botón: ListModal del chasis, SOLO lectura (sin acciones por
 * fila — tomar sigue siendo el botón grande de la tarjeta navy). Consume el
 * MISMO endpoint que la QueuePage vieja (`GET /agents/me/queue`), sin PHI:
 * cinHash + intentos + último intento + estado. (MOVIDO de QueuePage tal
 * cual en F5-R9a.)
 */
function QueueListModal({
  open,
  onClose,
  total,
  hasActive,
  t,
}: {
  open: boolean;
  onClose: () => void;
  total: number;
  /** R2: con llamada abierta las filas quedan inertes (la regla de la navy). */
  hasActive: boolean;
  t: ReturnType<typeof useT>;
}): JSX.Element | null {
  const navigate = useNavigate();
  const setCurrent = useActiveCallStore((s) => s.setCurrent);
  const claimMut = useClaimSpecific();
  // COLA-AGENTE A1 — una pagina de VERDAD contra el servidor.
  //
  // Antes: `{ limit: 200, offset: 0 }` fijo. El servidor recorta el limit a
  // 100 y el chasis partia esas 100 en paginas de 25 = 4 paginas exactas,
  // siempre las mismas, sobre una cola de miles. Ahora el offset se mueve.
  const [pagina, setPagina] = useState(0);
  const listQ = useQuery({
    queryKey: ['queue', 'me', 'r1a-list', pagina],
    queryFn: () => queueApi.list({ limit: QUEUE_PAGE, offset: pagina * QUEUE_PAGE }),
    enabled: open,
    staleTime: 30_000,
    placeholderData: (previa) => previa,
  });

  // R2 (caminata CEO): «la cola debe ser seleccionable a partir de ahí» —
  // click en la fila reclama ESE paciente por el claim que ya existía en el
  // server (claimSpecific, el camino de la QueuePage vieja) y te lleva a su
  // pre-llamada. Con llamada abierta no se puede (misma regla de la navy).
  const abrir = (assignmentId: string, status: string): void => {
    if (claimMut.isPending) return;
    if (status === 'in_progress') {
      onClose();
      navigate(`/pre-call/${assignmentId}`);
      return;
    }
    claimMut.mutate(assignmentId, {
      onSuccess: (data) => {
        setCurrent(data);
        onClose();
        navigate(`/pre-call/${assignmentId}`);
      },
    });
  };

  const lastAttemptLabel = (row: QueueItem): string => {
    if (row.attemptCount === 0 || !row.lastAttemptAt) return t('queue.pending.never');
    const min = Math.floor((Date.now() - new Date(row.lastAttemptAt).getTime()) / 60_000);
    if (min < 1) return t('queue.pending.now');
    if (min < 60) return t('queue.pending.minAgo', { n: min });
    const hr = Math.floor(min / 60);
    if (hr < 24) return t('queue.pending.hAgo', { n: hr });
    return t('queue.pending.dAgo', { n: Math.floor(hr / 24) });
  };

  // OJO CON EL ORDEN: `maxAttempts` se usa DENTRO del `.map` de abajo, y ese
  // map corre en el acto. Declararlo DESPUES lo deja en la zona muerta
  // temporal y la pantalla revienta entera con «Cannot access 'S' before
  // initialization». Paso en produccion el 2026-09-02: lo vio el CEO al abrir
  // «Mi cola», y no lo cazo ni el typecheck ni los 1.977 checks de la
  // bateria — solo aparece cuando el componente RENDERIZA de verdad.
  const maxAttempts = listQ.data?.maxCallAttempts;

  const rows: ListModalRow[] = (listQ.data?.queue ?? []).map((q) => ({
    id: q.assignmentId,
    title: `…${shortCinHash(q.cinHash)}`,
    // COLA-AGENTE A4 — «le quedan N intentos». El miedo que reportaron por
    // correo era a «bloquear el numero» con el quinto: no se bloquea nada, el
    // paciente sale de la cola. Decirlo en la fila saca el miedo del medio.
    // El tope viene del server (`maxAttempts`), NUNCA de un numero escrito
    // a mano: es configurable y ya cambio antes.
    meta: `${
      q.attemptCount === 1
        ? t('agentF5.r1a.rowAttemptsOne')
        : t('agentF5.r1a.rowAttempts', { n: q.attemptCount })
    } · ${lastAttemptLabel(q)}${
      typeof maxAttempts === 'number' && maxAttempts > q.attemptCount
        ? ` · ${t('cola.rowLeft', { n: maxAttempts - q.attemptCount })}`
        : ''
    }`,
    onClick: hasActive ? undefined : () => abrir(q.assignmentId, q.status),
    chip: (
      <NavPill tone={q.status === 'in_progress' ? 'teal' : 'grey'}>
        {q.status === 'in_progress' ? t('queue.statusInProgress') : t('queue.statusPending')}
      </NavPill>
    ),
  }));

  const count = listQ.data?.total ?? total;
  return (
    <ListModalChassis
      open={open}
      pageSize={QUEUE_PAGE}
      totalRows={listQ.data?.total ?? 0}
      onPageChange={setPagina}
      wide
      title={t('agentF5.r1a.queueModalTitle')}
      countLabel={
        count === 1
          ? t('agentF5.r1a.queueModalCountOne')
          : t('agentF5.r1a.queueModalCount', { n: count })
      }
      rows={rows}
      loading={listQ.isLoading}
      error={!!listQ.error}
      emptyTitle={t('agentF5.r1a.queueEmptyTitle')}
      emptyBody={t('agentF5.r1a.queueEmptyBody')}
      errorTitle={t('agentF5.r1a.queueErrTitle')}
      errorBody={t('agentF5.r1a.queueErrBody')}
      retryLabel={t('agentF5.r1a.queueRetry')}
      onRetry={() => void listQ.refetch()}
      onClose={onClose}
      closeAria={t('common.close')}
      pagerLabels={{
        prev: t('agentF5.r1a.pagerPrev'),
        next: t('agentF5.r1a.pagerNext'),
        pageOf: (page, pages) => t('agentF5.r1a.pagerPage', { p: page, pp: pages }),
      }}
      filter={
        <span className="hidden md:block max-w-[220px] text-right font-body text-[11px] text-nav-slate leading-tight">
          {t('agentF5.r1a.queueModalHint')}
        </span>
      }
    />
  );
}

/**
 * Búsqueda al pie: ID de Medicaid COMPLETO o teléfono COMPLETO (sin parcial,
 * sin nombre — privacidad). Dispara con ≥4 caracteres, como el mockup.
 * El estado «parqueado / Desbloquear y abrir» necesita que el server exponga
 * el estado del caso en el resultado — hasta entonces, «Abrir» al perfil.
 * (MOVIDA de QueuePage tal cual en F5-R9a.)
 */
function CaseSearch({ t }: { t: ReturnType<typeof useT> }): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PatientSearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 4) {
      setResults(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const handle = setTimeout(() => {
      setLoading(true);
      clientsApi
        .searchPatients(q, 20, controller.signal)
        .then((rows) => {
          if (controller.signal.aborted) return;
          setResults(rows);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setResults([]);
        })
        .finally(() => {
          if (controller.signal.aborted) return;
          setLoading(false);
        });
    }, 400);
    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [query]);

  return (
    <section className="animate-fade-up-slow" style={{ animationDelay: '120ms' }}>
      <Kicker className="mt-[30px] mb-3.5">{t('agentF5.q.searchKicker')}</Kicker>
      <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-[22px] py-5">
        <div className="flex gap-2.5 flex-wrap">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('agentF5.q.searchPlaceholder')}
            aria-label={t('agentF5.q.searchAria')}
            className={cn(INP, 'flex-1 min-w-[260px] h-11')}
          />
          <button
            type="button"
            onClick={() => setQuery('')}
            className="min-h-[44px] px-4 rounded-lg bg-white border-[1.5px] border-nav-line font-head text-[13px] font-bold text-nav-navy cursor-pointer hover:bg-nav-soft hover:border-nav-slate transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal"
          >
            {t('agentF5.q.searchClear')}
          </button>
        </div>
        {loading && (
          <div className="mt-3 space-y-2" aria-busy="true">
            <Skeleton className="h-[60px] w-full" />
          </div>
        )}
        {!loading && results && results.length > 0 && (
          <div className="mt-3 border-[1.5px] border-nav-card rounded-[10px] overflow-hidden">
            {results.map((r, i) => (
              <div
                key={r.id}
                className={cn(
                  'flex items-center gap-3 flex-wrap min-h-[60px] px-3.5 py-2.5',
                  i < results.length - 1 && 'border-b border-nav-hair',
                )}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-head text-[13.5px] font-bold m-0 truncate">
                    {r.firstName} {r.lastName}
                  </p>
                  <p className="text-[12.5px] text-nav-slate tabular-nums m-0">
                    {t('agentF5.q.searchMeta', { id: r.cinLast6, dob: r.dob })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/clients/${r.id}`)}
                  className="shrink-0 min-h-[44px] px-4 rounded-lg bg-white border-[1.5px] border-nav-line font-head text-[13px] font-bold text-nav-navy cursor-pointer hover:border-nav-teal hover:text-nav-tealDark transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal"
                >
                  {t('agentF5.q.searchOpen')}
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[12.5px] text-nav-slate mt-3 m-0" aria-live="polite">
          {!results
            ? t('agentF5.q.searchNoteIdle')
            : results.length === 0 && !loading
              ? t('agentF5.q.searchNoteNone')
              : results.length === 1
                ? t('agentF5.q.searchNoteOne')
                : t('agentF5.q.searchNoteResults', { n: results.length })}
        </p>
      </div>
    </section>
  );
}
